import { CommonModule } from "@angular/common";
import {
    Component,
    ElementRef,
    HostBinding,
    afterNextRender,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    model,
    signal,
    viewChild,
    viewChildren,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Capacitor } from "@capacitor/core";
import { OptionDirective } from "../../directives/option.directive";
import { ScrollGuideDirective } from "../../directives/scroll-guide.directive";
import { BaseConfig } from "../common/config/config.adapter";
import { Color } from "../common/types";
import { SegmentOptionComponent } from "./option/segment-option.component";

export type SegmentOption = {
    label: string;
    value: string;
    icon?: string;
    count?: number;
    renderItem?: any;
};

@Component({
    selector: "app-segment",
    templateUrl: "./segment.component.html",
    styleUrls: ["./segment.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        OptionDirective,
        SegmentOptionComponent,
        ScrollGuideDirective,
    ],
})
export class Segment extends BaseConfig {
    @HostBinding("class") get hostClass() {
        if (this.scrollEnabled()) {
            return "w-full";
        }

        return "";
    }

    readonly elementRef = inject(ElementRef);

    container = viewChild<ElementRef<HTMLElement>>("container");
    indicator = viewChild<ElementRef<HTMLElement>>("indicator");
    items = viewChildren<SegmentOptionComponent>("item");

    platform = Capacitor.getPlatform();
    color = input<Color>("primary");
    $options = model<string[] | SegmentOption[]>([], { alias: "options" });

    options = computed<SegmentOption[]>(() => {
        if (this.$options().length === 0) return [];

        return this.$options().map((option) => {
            if (typeof option === "string") {
                return {
                    label: option,
                    value: option,
                };
            }

            return option;
        });
    });

    value = model<string>(this.options()[0]?.value);
    value$ = toObservable(this.value);

    noBorder = input<boolean, string>(false, { transform: booleanAttribute });

    scrollEnd = signal(false);

    scrollEnabled = input<boolean, string>(false, {
        transform: booleanAttribute,
    });

    constructor() {
        super();

        afterNextRender(() => {
            if (!this.value() && this.options()?.length > 0) {
                this.value.set(this.options()[0]?.value);
            }

            this.value$.subscribe({
                next: (value) => {
                    if (this.container() && value && this.scrollEnabled()) {
                        // setTimeout 안걸면 scrollBy가 안먹힘
                        setTimeout(() => {
                            const left = this.getTranslateX(
                                this.options().findIndex(
                                    (item) => item.value === value,
                                ),
                            );
                            this.container()!.nativeElement.scrollBy({
                                left,
                            });
                        }, 100);
                    }
                },
            });
        });

        effect(() => {
            if (this.items() && this.indicator() && this.value()) {
                this.transform();
            }
        });
    }

    handleClick(option: SegmentOption) {
        this.value.set(option.value);
    }

    transform() {
        const index = this.options().findIndex(
            (item) => item.value === this.value(),
        );
        if (index === -1) return;
        this.indicator()!.nativeElement.style.width = `${this.items()[index].elementRef.nativeElement!.offsetWidth}px`;
        const translateX = this.getTranslateX(index);
        this.indicator()!.nativeElement.style.transform = `translateX(${translateX}px)`;
    }

    getTranslateX(index: number) {
        return this.items()
            .slice(0, index)
            .reduce(
                (acc, cur) => acc + cur.elementRef.nativeElement.offsetWidth,
                0,
            );
    }

    handleScroll(ev: any) {
        // 맨 끝으로 스크롤 했을 때 감지
        const scrollLeft = ev.target.scrollLeft;
        const scrollWidth = ev.target.scrollWidth;
        const clientWidth = ev.target.clientWidth;
        const scrollEnd = scrollLeft + clientWidth >= scrollWidth - 1;
        this.scrollEnd.set(scrollEnd);
    }
}
